<template>
  <div>
    <ts-loading-banner :loading="loading">
      <div style="min-height: 30vh">
        <table>
          <thead>
            <tr>
              <th class="ts-text-xs ts-w-1/3">
                {{ $t('studentScoringComponentEntry.subject') }} :
                {{ subjectName }}
              </th>
              <th class="ts-text-xs ts-text-right ts-w-2/3">
                {{ $t('studentScoringComponentEntry.actualScore') }}
              </th>
              <th class="ts-text-xs text-center ts-w-32">
                {{ $t('studentScoringEntry.maxScore') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(mainData, index) in dataInput">
              <tr :key="index">
                <td class="ts-text-sm text-left">
                  <span class="badge badge-primary">
                    <p>{{ mainData.comp_item_name }}</p>
                  </span>
                </td>
              </tr>
              <tr v-for="(sub, index) in mainData.component_items">
                <td class="ts-text-right ts-text-xs">
                  {{ sub.subject_comp_item_detail_name }}
                </td>
                <td>
                  <input
                    type="number"
                    v-model.number="sub.actual_score"
                    class="ts-text-right ts-w-full form-control form-control-sm"
                    max="20"
                    :class="{
                      'is-invalid': errors.has(
                        'component_items.' +
                          sub.subject_comp_detail_id +
                          '.actual_score'
                      )
                    }"
                  />
                </td>
                <td
                  class="ts-whitespace-no-wrap"
                  @dblclick="() => (sub.disable = false)"
                >
                  <input
                    type="number"
                    class="text-right inline-block ts-w-16 form-control form-control-sm"
                    disabled
                    :value="sub.max_score"
                  />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div class="row mt-3">
        <div class="col-md-8 d-flex align-items-center">
          <p class="text-danger" v-if="show_alert">
            <i class="fas fa-exclamation-triangle"></i>
            <span class="ts-text-xs">Something went wrong.</span>
          </p>
        </div>
        <div class="col-md-4 text-right ts-flex">
          <ts-button class="mr-2" @click.prevent="$emit('closeModal')">{{
            $t('cancel')
          }}</ts-button>
          <ts-button
            color="primary"
            @click.prevent="onSave"
            :waiting="waiting"
            >{{ $t('save') }}</ts-button
          >
        </div>
      </div>
    </ts-loading-banner>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { Errors } from 'form-backend-validation'
import { isEmpty } from 'lodash'

export default {
  name: 'studentScoringComponentEntryNumeric',
  props: ['value'],
  data() {
    return {
      waiting: false,
      loading: false,
      show_alert: false,
      errors: new Errors(),
      scoreComponents: [],
      model: {},
      dataInput: [],
      successCreate: [],
      subjectComponentItemDetails: [],
      actual_score_for_main_component_display: [] // for display max score on main component
    }
  },
  created() {
    this.model = this.value
    this.fetchData()
  },
  computed: {
    ...mapState('studentScoringComponentEntry', ['subjects']),
    subjectName() {
      return this.subjects.find(s => s.value == this.model.subject_id).label
    }
  },
  methods: {
    ...mapActions('studentScoringComponentEntry', [
      'store',
      'getSubjectComponentItems',
      'getSubjectComponentItemDetails'
    ]),
    async fetchData() {
      this.loading = true
      await this.getSubjectComponentItems({
        // get component base on subject
        subject_id: this.model.subject_id
      })
        .then(response => {
          this.createInputData(response.data)
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })

      // find subject component item details
      await this.getSubjectComponentItemDetails(this.model)
        .then(response => {
          this.subjectComponentItemDetails = []
          if (!isEmpty(response.data) || response.data.length > 0) {
            this.subjectComponentItemDetails = response.data
            this.inputEditData()
          }
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    inputEditData() {
      this.dataInput.forEach(e => {
        e.component_items.forEach(ec => {
          if (
            this.subjectComponentItemDetails.find(
              el => el.subject_comp_detail_id == ec.subject_comp_detail_id
            )
          ) {
            ec.actual_score = this.subjectComponentItemDetails.find(
              el => el.subject_comp_detail_id == ec.subject_comp_detail_id
            ).actual_score

            ec.scoring_id = this.subjectComponentItemDetails.find(
              el => el.subject_comp_detail_id == ec.subject_comp_detail_id
            ).scoring_id
          }
        })
      })
    },
    createInputData(data) {
      // Create Input Data
      data.forEach(e => {
        this.dataInput.push({
          student_id: this.model.student_id,
          st_class_id: this.model.st_class_id,
          scoring_cycle_id: this.model.scoring_cycle_id,
          cycle_value: this.model.cycle_value,
          subject_comp_id: e.subject_comp_id,
          subject_id: e.subject_id,
          comp_item_name: e.comp_item_name,
          component_items: e['component_items'].map(el => ({
            scoring_id: el.scoring_id ? el.scoring_id : 0,
            subject_comp_detail_id: el.subject_comp_detail_id,
            subject_comp_item_detail_name: el.subject_comp_item_detail_name,
            actual_score: -1,
            max_score: el.max_score
          }))
        })
      })
    },
    async onSave() {
      this.errors = new Errors()
      this.waiting = true
      this.successCreate = []
      this.show_alert = false
      for (const [index, item] of this.dataInput.entries()) {
        await this.store(
          Object.assign(item, {
            is_last_row: index === this.dataInput.length - 1
          })
        )
          .then(() => {
            this.successCreate.push(item)
            if (index === this.dataInput.length - 1) {
              this.waiting = false
              this.$emit('closeModal')
              this.$toasted.success('Action completed successfully!')
            }
          })
          .catch(error => {
            this.show_alert = true
            this.waiting = false
            this.$toasted.error(error.message)
            if (error.errors != undefined) {
              this.errors = new Errors(error.errors)
            }
          })
        if (this.show_alert) {
          break
        }
      }
      this.successCreate.forEach(async el => {
        await this.dataInput.splice(this.dataInput.indexOf(el), 1)
      })
    }
  }
}
</script>

<style lang="css" scoped>
table {
  width: 100%;
}
table > thead {
  border-bottom: 1px solid #d8d8d8;
  padding: 3px;
}
table > thead > tr > th {
  padding: 4px 7px;
}
table > tbody > tr > td {
  padding: 4px 7px;
}
.bb {
  border-bottom: 1px solid #d8d8d8;
  outline: none;
}
</style>
